import React, { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ErrorView } from '../../../views/error/ErrorView'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../common/Illustration'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { BookingListItem } from './BookingListItem'
import { unitQueries } from '../../../api/units'
import { useAuth } from '../../../hooks/useAuth'
import { bookingQueries } from '../../../api/bookings'

export const BookingsList = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const { society } = useCurrentSociety()
  const { data: userUnits, isLoading: isLoadingUserUnits } = useQuery(
    unitQueries.getUserUnits(userId)
  )
  const userUnitsInSociety = userUnits?.filter(
    (_unit) => _unit.societyId === society?._id
  )

  const date = useMemo(
    () => dayjs().set('hour', 0).set('seconds', 0).toDate(),
    []
  )

  const {
    data: futureUserBookingsInSociety,
    isLoading: isLoadingUserBookings,
  } = useQueries({
    queries:
      userUnitsInSociety?.map((_unit) => {
        return bookingQueries.getBookingsPerUnit(_unit._id, date)
      }) || [],

    combine: (results) => {
      return {
        data: results.flatMap((query) => query.data || []),
        isLoading: results.some((query) => query.isPending),
      }
    },
  })

  const loading = isLoadingUserUnits || isLoadingUserBookings

  if (loading) {
    return <SpinnerWrapper />
  }

  if (!loading && futureUserBookingsInSociety.length === 0) {
    return (
      <ErrorView
        title={translate('facilitiesView.emptyState.noBookingsTitle')}
        subtitle={translate('facilitiesView.emptyState.noBookingsSubtitle')}
        illustration={IllustrationChoices.CHECKLIST}
        titleWrapperClassName="mt-8"
      />
    )
  }

  return (
    <div className="w-full space-y-4">
      {futureUserBookingsInSociety.map((booking) => (
        <BookingListItem key={booking._id} booking={booking} />
      ))}
    </div>
  )
}
